import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { finalize, first, Observable, switchMap, tap, debounceTime, distinctUntilChanged } from 'rxjs';
import { Client, ActionItemDto, ActionItemDtoPagedResult } from '../../../api/api-clientInformation';
import { LoaderService } from '../../../services/loader.service';
import { SearchStorageService } from '../../../services/search-storage.service';
import { ConfirmationDialogComponent, ConfirmationDialogPayload, DropdownComponent, DropdownOption, DropdownOptionValue, FilterResponse, TableColumnFilterComponent, ToolbarComponent } from '../../../shared';
import { ActionItemTypeOptions, ActionItemWorkScopeOptions } from '../../../utils/enum-maps';
import { StripHtmlPipe } from '../../../shared/pipes/strip-html.pipe';
import { ColumnResizeDirective } from '../../../shared/directives';
import { filterTable, utcToLocal } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';

enum ActionItemStatusFilter {
  ACTIVE,
  INACTIVE,
}

@Component({
  selector: 'app-action-item-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule, MatButtonModule, MatTableModule, MatSortModule,
    MatFormFieldModule, MatInputModule, MatCheckboxModule, MatPaginatorModule,
      MatSnackBarModule, ToolbarComponent, StripHtmlPipe, ColumnResizeDirective,

      // Components
    TableColumnFilterComponent
  ],
  templateUrl: './action-item-list.component.html',
  styleUrls: ['./action-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ActionItemListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  private readonly COMPONENT_NAME = 'action-item-list';

  displayedColumns: string[] = [
    'select','ID', 'actionItemDescriptionEn', 'actionItemOwner', 'dueDate', 'status', 'actionType', 'workScope', 'progress', 'createdOn', 'completedOn'
  ];
  pageIndex: number = 0;
  pageSize: number = 25;
  totalCount: number = 100;
  totalFilterCount: number = 100;
  pageSizeOptions: number[] = [25, 50, 75, 100];

  // Expose Math to template
  Math = Math;

  dataSource = new MatTableDataSource<ActionItemDto>();
  allRecords: ActionItemDto[] = [];
  selection = new SelectionModel<ActionItemDto>(true, []);
  searchControl = new FormControl<string>('');

  // filterOptions = signal<DropdownOption<ActionItemStatusFilter>[]>([
  //   { title: 'Active Action Items', value: ActionItemStatusFilter.ACTIVE },
  //   { title: 'Inactive Action Items', value: ActionItemStatusFilter.INACTIVE },
  // ]);
  activeFilter = signal<ActionItemStatusFilter>(ActionItemStatusFilter.ACTIVE);

  actionTypeLabelMap: { [key: number]: string } = {};
  workScopeLabelMap: { [key: number]: string } = {};

  private apiClient = inject(Client);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private loader = inject(LoaderService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private searchStorage = inject(SearchStorageService);

  ngOnInit(): void {
    this.labelMaps();
    this.dataSourceFiltering();
    this.loadSearchFromStorage();
    this.searchControlFilter();
    const savedSearch = this.searchControl.value || '';
    this.loadReports(savedSearch.trim());
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;

    this.sort.sort({
      id: 'createdOn',
      start: 'desc',
      disableClear: false
    });

    // Handle sort changes for custom pagination when not searching
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentSearch = this.searchControl.value || '';
      if (!currentSearch.trim()) {
        // When not searching and using backend pagination, reload from server
        this.loadReports();
      }
    });
  }

  private labelMaps(): void {
    this.actionTypeLabelMap = ActionItemTypeOptions.reduce((acc, curr) => {
      acc[curr.value as unknown as number] = curr.label;
      return acc;
    }, {} as { [key: number]: string });

    this.workScopeLabelMap = ActionItemWorkScopeOptions.reduce((acc, curr) => {
      acc[curr.value as unknown as number] = curr.label;
      return acc;
    }, {} as { [key: number]: string });
  }

  private loadSearchFromStorage(): void {
    const savedSearch = this.searchStorage.getSearch(this.COMPONENT_NAME);
    if (savedSearch) {
      this.searchControl.setValue(savedSearch, { emitEvent: false });
    }
  }

  private dataSourceFiltering(): void {
    this.dataSource.filterPredicate = (data: ActionItemDto, filter: string) => {
      if (!filter.trim()) return true;

      const searchText = filter.toLowerCase();
      const searchableText = [
        data.deviationReportID || '',
        data.actionItemId || '',
        data.actionItemOwner || '',
        data.actionItemDescriptionEn || '',
        data.status || '',
        data.dueDate ? new Date(data.dueDate).toLocaleDateString() : '',
        this.actionTypeLabelMap[data.actionType as unknown as number] || '',
        this.workScopeLabelMap[data.workScope as unknown as number] || ''
      ].join(' ').toLowerCase();

      return searchableText.includes(searchText);
    };
  }

  private searchControlFilter(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(searchText => {
      const searchValue = searchText || '';

      // Save to localStorage via service
      this.searchStorage.saveSearch(this.COMPONENT_NAME, searchValue);

      // Reset to first page when search changes
      this.pageIndex = 0;

      // Reload data with appropriate pagination based on search
      this.loadReports(searchValue.trim());
    });
  }

  loadActionItems(searchText: string = ''): Observable<ActionItemDtoPagedResult> {
    // When searching, fetch all records (use large page size); otherwise use normal pagination
    const isSearching = searchText.trim().length > 0;
    const pageNumber = isSearching ? 1 : this.pageIndex + 1;
    const pageSize = isSearching ? 10000 : this.pageSize;

    return this.apiClient.paged(pageNumber, pageSize).pipe(
      tap(records => {
        this.totalCount = records.totalCount || 0;
        this.allRecords = records.items?.map(item => ActionItemDto.fromJS({
          ...item,
          createdOn: utcToLocal(item.createdOn)?.toDate(),
          completedOn: utcToLocal(item.completedOn)?.toDate(),
          dueDate: utcToLocal(item.dueDate)?.toDate(),
        })) || [];

        if (isSearching) {
          // Apply search filter
          this.dataSource.data = this.allRecords;
          this.tempTableData = [...this.dataSource.data];
          this.dataSource.filter = searchText;
          this.totalFilterCount = this.dataSource.filteredData.length;
        } else {
          // Normal pagination - no filter
          this.totalFilterCount = this.totalCount;
          this.applyStatusFilter();
        }

        this.cdr.markForCheck();
      })
    );
  }

  loadReports(searchText: string = ''): void {
    this.loader.startLoading();
    this.loadActionItems(searchText)
      .pipe(
        finalize(() => {
          this.loader.stopLoading();
          this.cdr.markForCheck();
        }),
        first()
      )
      .subscribe();
  }

  filterRecords(status: DropdownOptionValue): void {
    this.activeFilter.set(status as ActionItemStatusFilter);
    this.applyStatusFilter();
  }

  private applyStatusFilter(): void {
    const statusFilter = this.activeFilter();
    let filteredData = this.allRecords;

    // if (statusFilter === ActionItemStatusFilter.ACTIVE) {
    //   filteredData = this.allRecords.filter(item => item.status !== 'Inactive');
    // } else {
    //   filteredData = this.allRecords.filter(item => item.status === 'Inactive');
    // }

    this.dataSource.data = this.allRecords;
    this.tempTableData = [...this.dataSource.data];
    this.selection.clear();

    this.dataSource.filter = this.searchControl.value || '';
  }

  isAllSelected(): boolean {
    const selectableRows = this.dataSource.filteredData //.filter(row => row.status !== 'Inactive');
    return selectableRows.length > 0 && this.selection.selected.length === selectableRows.length;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.filteredData.forEach(row => {
        this.selection.select(row);
        // if (row.status !== 'Inactive') {
        // }
      });
    }
  }

  canDelete(): boolean {
    return false;
    // return this.selection.selected.length === 1 && this.selection.selected[0].status === 'Active';
  }

  canDeactivate(): boolean {
    return false;
    // return this.selection.selected.length === 1 && this.selection.selected[0].status === 'Active';
  }

  canActivate(): boolean {
    return false;
    // return this.selection.selected.length === 1 && this.selection.selected[0].status === 'Inactive';
  }

  confirmDelete(): void {
    if (!this.canDelete()) return;

    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogPayload, boolean>(
      ConfirmationDialogComponent, {
      data: { title: 'Confirm Deletion', message: 'Are you sure you want to delete this action item?' }
    }
    );

    dialogRef.afterClosed().pipe(first()).subscribe(isConfirmed => {
      if (isConfirmed) {
        this.delete();
      }
    });
  }

  private delete(): void {
    const id = this.selection.selected[0].id;
    if (!id) return;

    this.loader.startProcessing();
    const currentSearch = this.searchControl.value || '';
    this.apiClient.actionItemDELETE(id).pipe(
      switchMap(() => {
        this.toastr.success('Item deleted successfully!', 'Close', { timeOut: 3000 });
        this.selection.clear();
        return this.loadActionItems(currentSearch.trim());
      }),
      finalize(() => {
        this.loader.stopProcessing();
        this.cdr.markForCheck();
      }),
      first()
    ).subscribe();
  }

  activate(): void {
    if (!this.canActivate()) return;
    this.toastr.info('Activate functionality not implemented.', 'Close', { timeOut: 3000 });
  }

  deactivate(): void {
    if (!this.canDeactivate()) return;
    this.toastr.info('Deactivate functionality not implemented.', 'Close', { timeOut: 3000 });
  }

  // Custom pagination methods
  onPageChange(page: number): void {
    this.pageIndex = page - 1; // Convert to 0-based index
    const currentSearch = this.searchControl.value || '';
    this.loadReports(currentSearch.trim());
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 0; // Reset to first page when changing page size
    const currentSearch = this.searchControl.value || '';
    this.loadReports(currentSearch.trim());
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalFilterCount / this.pageSize));
  }

  get currentPage(): number {
    return this.pageIndex + 1; // Convert to 1-based for display
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const total = this.totalPages;
    const current = this.currentPage;

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 3) {
        // Near the beginning
        pages.push(2, 3, 4, -1, total); // -1 represents ellipsis
      } else if (current >= total - 2) {
        // Near the end
        pages.push(-1, total - 3, total - 2, total - 1, total);
      } else {
        // In the middle
        pages.push(-1, current - 1, current, current + 1, -1, total);
      }
    }

    return pages;
  }

  goToFirstPage(): void {
    if (this.currentPage !== 1) {
      this.onPageChange(1);
    }
  }

  goToLastPage(): void {
    if (this.currentPage !== this.totalPages) {
      this.onPageChange(this.totalPages);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.onPageChange(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.currentPage + 1);
    }
  }

  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.dataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
